import { html } from '@shared/utils/rendering/html';
import { useMemo } from 'preact/hooks';
import { BarChart } from '@shared/components/charts';
import { CHART_COLORS } from '@shared/constants/chart-palette';
import { ErrorBoundary } from '@core/components/shared/error-boundary';
import { buildChartTooltip } from '@core/utils/helpers/chart-tooltip-builder';
import './components-chart.scss';

const BAR_SLOT_PX = 35;
const CHART_PADDING_PX = 80;

export const ComponentsChart = ({
    componentRatings = [],
    // Component -> its principles, inverted from the server's principle/component mapping by the
    // parent. Previously a hardcoded literal in this file that had drifted out of step with the
    // mapping the principle ratings were built from.
    componentPrincipleMap = {},
    isLoading = false,
    selectedPrinciple = null,
}) => {
    const displayedRatings = useMemo(
        () =>
            selectedPrinciple
                ? componentRatings.filter((cr) =>
                      (componentPrincipleMap[cr.componentId] ?? []).includes(
                          parseInt(selectedPrinciple)
                      )
                  )
                : componentRatings,
        // componentPrincipleMap is fetched, so it arrives after the first render - without it here
        // the filtered list would keep the empty-map result.
        [componentRatings, selectedPrinciple, componentPrincipleMap]
    );

    const chartData = useMemo(() => {
        if (!displayedRatings || displayedRatings.length === 0) return null;

        const sortedRatings = [...displayedRatings].sort((a, b) => a.componentId - b.componentId);
        const categories = sortedRatings.map((cr) => {
            const pct = cr.totalQuestions
                ? Math.round((cr.completedQuestions / cr.totalQuestions) * 100)
                : 0;
            return [cr.componentName || `Component ${cr.componentId}`, `${pct}% Completed`];
        });
        // Method A ratings are already 1dp - display them that way (2.3, not 2.30).
        const values = sortedRatings.map((cr) =>
            cr.averageRating ? parseFloat(cr.averageRating.toFixed(1)) : 0
        );

        return { categories, datasets: [{ name: 'Average Rating', values }] };
    }, [displayedRatings]);

    const chartConfig = useMemo(() => {
        const numBars = displayedRatings.length || 26;
        const chartHeight = Math.max(400, numBars * BAR_SLOT_PX + CHART_PADDING_PX);

        return {
            horizontal: true,
            stacked: false,
            borderRadius: 4,
            columnWidth: '70%',
            showDataLabels: false,
            overrides: {
                colors: [CHART_COLORS.primary],
                chart: {
                    height: chartHeight,
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        borderRadius: 5,
                        borderRadiusApplication: 'end',
                        barHeight: '55%',
                    },
                },
                stroke: { show: true, width: 2, colors: ['transparent'] },
                xaxis: {
                    min: 0,
                    max: 4.5,
                    tickAmount: 9,
                    labels: { formatter: (val) => val?.toFixed(1) },
                },
                yaxis: {
                    labels: { style: { fontSize: '13px' }, maxWidth: 400 },
                },
                dataLabels: { enabled: false },
                tooltip: {
                    enabled: true,
                    custom: ({ series, seriesIndex, dataPointIndex }) => {
                        const sortedRatings = [...displayedRatings].sort(
                            (a, b) => a.componentId - b.componentId
                        );
                        const rating = sortedRatings[dataPointIndex];
                        if (!rating) return '';
                        const avgRating = series[seriesIndex][dataPointIndex]?.toFixed(1) || '0.0';
                        const pct = rating.totalQuestions
                            ? Math.round((rating.completedQuestions / rating.totalQuestions) * 100)
                            : 0;
                        return buildChartTooltip({
                            header: rating.componentName,
                            metrics: [
                                { label: 'Average', value: avgRating },
                                { label: 'Completed', value: `${pct}%` },
                            ],
                        });
                    },
                },
            },
        };
    }, [displayedRatings]);

    return html`
        <${ErrorBoundary} fallback=${html`<p>Unable to display components chart.</p>`}>
            <${BarChart}
                data=${chartData}
                config=${chartConfig}
                isLoading=${isLoading}
                noDataMessage="No results to show — no components have been assessed yet"
                css="sarb-components-chart"
            />
        <//>
    `;
};
